import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Box, Copy, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import DealerDetailsButton from './DealerDetailsButton';
import DealerLocation from '../shared/DealerLocation';
import DealerScheduleServiceButton from './DealerScheduleServiceButton';

import Hours from './Hours';
import SelectDealerButton from './SelectDealerButton';

import dealerPropTypes from '../../utils/interfaces';
import formatContactNumber from '../../utils/formatContactNumber';
import formatContactUrlDomain from '../../utils/formatContactURLDomain';
import formatDrivingDistance from '../../utils/formatDrivingDistance';
import formatDealerAddress from '../../utils/formatDealerAddress';
import getProductLinesInfo from '../../utils/getProductLinesInfo';
import getSalesDeptContactInformation from '../../utils/getSalesDeptContactInformation';

import themeStyles from './DealerCard.style';
import DealerContact, { DealerContactDetail } from '../shared/DealerContact';
import ProductLines, { ContentDetails, ProductLinesTitle } from '../ProductLines';
import dealerCardGtmTags, { CLICKTO_CALL, WEBSITE } from '../../utils/gtmTags';
import useDealerCookie from '../../hooks/useDealerCookie';

import DealerBadges from '../shared/DealerBadges';
import variantsConfig from './variantsConfig';

const Container = themeStyles.apply(Box, 'Container');
const HeaderWrapper = themeStyles.apply(Box, 'HeaderWrapper');
const DealerName = themeStyles.apply(Copy, 'DealerName');
const Distance = themeStyles.apply(Copy, 'Distance');
const DealerInfoWrapper = themeStyles.apply(Box, 'DealerInfoWrapper');

const DealerCard = ({
  dealer,
  distance,
  isCardSelected,
  isCardHovered,
  handleDealerCardClick,
  handleScheduleServiceClick,
  handleDealerDetailsModalOpen,
  handleDealerCardMouseEnter,
  handleDealerCardMouseLeave,
  showSelectDealerCTA,
  variant,
  urlParams,
}) => {
  const { t } = useTranslation();

  const {
    name,
    contactInformation,
    departments,
    location,
    productLines,
    serviceBookingUrl,
    greenDealer,
    evDealer,
    code,
  } = dealer;

  const setDealerCookie = useDealerCookie();

  const { showProductLineCarried, showDealerWebsite, showScheduleServiceButton } =
    variantsConfig.getVariantConfig(variant);

  const phone = contactInformation?.phone || '';
  const website = contactInformation?.website || '';
  // get dealer sales department contact details
  const dealerContactInformation = getSalesDeptContactInformation(departments);
  const { dealerContact, dealerEmail, dealerWebsite } = dealerContactInformation;
  const dealerAddress = formatDealerAddress(location);

  const productLinesInfo = getProductLinesInfo(productLines);

  const handleClick = () => {
    setDealerCookie({
      dealerCode: code,
      dealerName: name,
      dealerContact,
      dealerEmail,
      dealerAddress,
      dealerWebsite,
      dealerDistance: distance,
      evDealer,
    });
  };

  return (
    <Container
      isCardSelected={isCardSelected || isCardHovered}
      data-testid="cy-dealer-card"
      onMouseEnter={handleDealerCardMouseEnter}
      onMouseLeave={handleDealerCardMouseLeave}
      onClick={handleDealerCardClick}
    >
      <HeaderWrapper variant={variant}>
        <DealerName data-testid="cy-dealer-name">{name}</DealerName>
        {Boolean(distance) && (
          <Distance data-testid="cy-dealer-distance">
            <span data-testid="cy-dealer-distance-value">{formatDrivingDistance(distance)}</span>
            <span data-testid="cy-dealer-distance-unit"> {t('Sub-apps.Dealers.kmLabel')}</span>
          </Distance>
        )}
      </HeaderWrapper>
      <DealerInfoWrapper>
        <Box display="flex">
          <Box>
            <DealerLocation address={location} hasBadge={greenDealer || evDealer} />
            <DealerContact>
              <DealerContactDetail
                gtmTags={dealerCardGtmTags?.Call}
                contact={formatContactNumber(phone)}
                prefix="tel"
                type="phone"
                gtmActionType={CLICKTO_CALL}
                dealer={dealer}
              />
            </DealerContact>
          </Box>
          <DealerBadges greenDealer={greenDealer} evDealer={evDealer} variant={variant} />
        </Box>
        <Optional when={showDealerWebsite}>
          <DealerContactDetail
            contact={formatContactUrlDomain(website)}
            type="website"
            gtmActionType={WEBSITE}
            dealer={dealer}
          />
        </Optional>
        <Hours departmentsAndHours={departments} />
        <Optional when={showProductLineCarried && !!productLinesInfo.length}>
          <ProductLines title={<ProductLinesTitle />}>
            {productLinesInfo.map(({ iconName, dictionaryLabel }) => (
              <ContentDetails
                key={iconName}
                iconName={iconName}
                title={t(`Sub-apps.Dealers.${dictionaryLabel}`)}
                testId={`cy-${iconName}-productLine`}
              />
            ))}
          </ProductLines>
        </Optional>
      </DealerInfoWrapper>
      {showSelectDealerCTA ? (
        <SelectDealerButton dealerCode={code} urlParams={urlParams} handleClick={handleClick} />
      ) : (
        <>
          <DealerDetailsButton onClick={handleDealerDetailsModalOpen} />
          <Optional when={showScheduleServiceButton && serviceBookingUrl}>
            <DealerScheduleServiceButton
              onClick={handleScheduleServiceClick}
              serviceUrl={serviceBookingUrl}
              dealerName={name}
              dealerCode={code}
              handleClick={handleClick}
            />
          </Optional>
        </>
      )}
    </Container>
  );
};

DealerCard.propTypes = {
  dealer: dealerPropTypes.isRequired,
  distance: PropTypes.number,
  handleDealerCardClick: PropTypes.func.isRequired,
  handleScheduleServiceClick: PropTypes.func.isRequired,
  handleDealerDetailsModalOpen: PropTypes.func.isRequired,
  handleDealerCardMouseEnter: PropTypes.func.isRequired,
  handleDealerCardMouseLeave: PropTypes.func.isRequired,
  isCardSelected: PropTypes.bool.isRequired,
  isCardHovered: PropTypes.bool.isRequired,
  showSelectDealerCTA: PropTypes.bool.isRequired,
  variant: PropTypes.string,
  urlParams: PropTypes.shape({
    queryParams: PropTypes.string,
    currentURLName: PropTypes.string.isRequired,
  }),
};

export default memo(DealerCard);
